/**
 * Custom hook for managing the basic details form of an event
 *
 * @returns {Object} An object containing the formik instance, state variables, and utility functions
 *
 * @process
 * 1. Initialize form state using Formik with data from Redux store
 * 2. Set up validation schema with Yup
 * 3. Determine if form should be in edit mode based on whether eventId exists
 * 4. Set up character counter for event short name
 * 5. Configure subcategory filtering based on selected category
 * 6. Handle form submission:
 *    - Process and format data for API
 *    - Determine if creating new event or updating existing
 *    - Only submit if changes detected in update mode
 *    - Dispatch appropriate Redux actions
 * 7. Provide utility functions for handling custom inputs and autocomplete components
 * 8. Track form changes compared to original data
 *
 * Error handling:
 * - Form validation through Yup schema
 * - Try/catch in submission to handle API errors
 * - Input validation for character limits
 */

import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEqual } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
    createEvent,
    nextTab,
    updateEventById
} from '../../../../../../app/store/slices/event/event';
import { trimAndCleanSpaces } from '../../../../../../utils/trimAndCleanSpaces';
import { errorToast } from '../../../../../../utils/toasts/toasts';

/**
 * category => other option id => 21
 * subcategory => other option id => 182
 * format => other option id => 39
 * tag => other option id => 4
 */

const filter = createFilterOptions();

export default function useBasicDetails() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const { user } = useSelector((state) => state.auth);
    const { subCategories } = useSelector((state) => state.eventSetup);
    const { eventData } = useSelector((state) => state.event);
    const [filteredSubcategories, setFilteredSubcategories] = useState([]);
    const [countCharacter, setCountCharacter] = useState(0);
    const [isEditModeOn, setIsEditModeOn] = useState(false);
    const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
    const [loading, setLoading] = useState(false);

    // Determine edit mode based on eventId
    useEffect(() => {
        // set true if eventId is not present
        const isEnableEditMode = eventData?.eventId ? false : true;
        setIsEditModeOn(isEnableEditMode);
    }, [eventData?.eventId]);

    useEffect(() => {
        const detailsValue = eventData?.is_publishable?.details;

        // Show edit button only when all conditions are met
        const shouldShow = eventData?.status === 'drafted' && detailsValue === 1;

        setShouldShowEditButton(shouldShow);
    }, [eventData?.status, eventData?.is_publishable]);

    // Function to check if there are changes in the form values compared to the original data
    function hasChanges(values, originalData) {
        const cleanedValues = {
            title: trimAndCleanSpaces(values.eventName),
            excerpt: trimAndCleanSpaces(values.eventShortName),
            category: values.category?.id,
            is_private: values.isEventPrivate,
            subcategories: values.subCategory.map((sub) => ({
                id: sub.id,
                value: sub.value || sub.name
            })),
            formats: values.format.map((format) => ({
                id: format.id,
                value: format.value || format.name
            })),
            online_event: !values.isPhysicalEvent
        };

        const cleanedOriginal = {
            title: trimAndCleanSpaces(originalData?.title || ''),
            excerpt: trimAndCleanSpaces(originalData?.excerpt || ''),
            category: originalData?.category?.id,
            is_private: originalData?.is_private,
            subcategories:
                originalData?.subcategories?.map((sub) => ({
                    id: sub.id,
                    value: sub.custom_value || sub.name
                })) || [],
            formats:
                originalData?.formats?.map((format) => ({
                    id: format.id,
                    value: format.custom_value || format.name
                })) || [],
            online_event: originalData?.online_event
        };

        // console.log("Cleaned Form Values:", cleanedValues);
        // console.log("Original Event Data:", cleanedOriginal);
        // console.log("Has Changes:", !isEqual(cleanedValues, cleanedOriginal));
        return !isEqual(cleanedValues, cleanedOriginal);
    }

    // initialize formik initial values
    const initialValues = {
        eventName: eventData?.title || '',
        eventShortName: eventData?.excerpt || '',
        isEventPrivate: eventData?.is_private ?? null,
        category: eventData?.category
            ? {
                id: eventData.category.id,
                name: eventData.category.name,
                value: eventData.category.custom_value || eventData.category.name
            }
            : '',
        subCategory: eventData?.subcategories
            ?.map((sub) =>
                subCategories.find((s) => s.id === sub.id) || {
                    id: sub.id,
                    name: sub.custom_value || sub.name,
                    isUserCreated: !!sub.custom_value, // custom tags
                }
            ) || [],
        format:
            eventData?.formats?.map((format) => ({
                id: format.id,
                name: format.name,
                value: format.custom_value || format.name
            })) || [],
        eventCreatedAs: '',
        isPhysicalEvent: !eventData?.online_event ?? null
    };
    // console.log('Initial Values for Basic Details Form:', JSON.stringify(initialValues, null, 2));

    // Add effect to handle URL update when event is created and has slug
    // useEffect(() => {
    //     if (eventData?.slug && params.eventSlug === 'new') {
    //         console.log("Updating URL with new event slug:", eventData.slug);
    //         // Replace 'new' with the actual slug in the URL
    //         const currentPath = window.location.pathname;
    //         const newPath = currentPath.replace('/new/', `/${eventData.slug}/`);
    //         navigate(newPath, { replace: true });
    //     }
    // }, [eventData?.slug, params.eventId, navigate]);

    // Initialize formik
    const formik = useFormik({
        initialValues,
        validationSchema,
        validateOnBlur: true,
        validationOnChange: true,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                // Filter formats to ensure they are in the correct format
                const filteredFormat = values.format.map((format) => {
                    if (format.id && !format.isUserCreated) {
                        return { id: format.id, value: format.value || format.name };
                    }

                    let customValue;
                    if (typeof format === 'string') {
                        customValue = format;
                    } else if (format.inputValue) {
                        customValue = format.inputValue;
                    } else if (format.isUserCreated && format.name) {
                        const match = format.name.match(/Add "([^"]+)"/);
                        customValue = match ? match[1] : format.name;
                    }
                    return { id: 39, value: customValue };
                });

                // Handle category selection
                const category =
                    values.category.id === 21
                        ? [{ id: 21, value: values.category?.value }]
                        : values.category?.id;

                const subCategories = values.subCategory.map((subCategory) => {
                    if (subCategory.id && !subCategory.isUserCreated) {
                        return { id: subCategory.id, value: subCategory.value || subCategory.name };
                    }

                    let customValue;
                    if (typeof subCategory === 'string') {
                        customValue = subCategory;
                    } else if (subCategory.inputValue) {
                        customValue = subCategory.inputValue;
                    } else if (subCategory.isUserCreated && subCategory.name) {
                        const match = subCategory.name.match(/Add "([^"]+)"/);
                        customValue = match ? match[1] : subCategory.name;
                    }
                    return { id: 182, value: customValue };
                });

                // Prepare payload for API
                const payload = {
                    step: 'details',
                    user_id: user.id,
                    title: trimAndCleanSpaces(values.eventName),
                    excerpt: trimAndCleanSpaces(values.eventShortName),
                    category: category,
                    is_private: values.isEventPrivate,
                    subcategories: subCategories,
                    formats: filteredFormat,
                    online_event: !values.isPhysicalEvent
                };

                // console.log("Payload for basic detail:", payload);

                if (eventData?.eventId) {
                    // Check if there are changes
                    if (hasChanges(values, eventData)) {
                        await dispatch(
                            updateEventById({
                                eventId: eventData.eventId,
                                eventData: payload
                            })
                        ).unwrap();
                    }
                } else {
                    const result = await dispatch(createEvent(payload)).unwrap();
                    console.log('Event created successfully:', result);

                    // If event was created successfully and has a slug, update URL
                    if (result?.data?.slug && params.eventSlug === 'new') {
                        const currentPath = window.location.pathname;
                        const newPath = currentPath.replace('/new/', `/${result.data.slug}/`);
                        navigate(newPath, { replace: true });
                    }
                }

                dispatch(nextTab());
            } catch (error) {
                errorToast(error?.message || 'Failed to update event details');
            } finally {
                setLoading(false);
            }
        }
    });

    // handle the event short name change with counter to avoid exceeding the character limit
    function handleShortNameChange(event) {
        const { value } = event.target;
        if (value.length <= characterLimit) {
            setCountCharacter(value.length);
            formik.setFieldValue('eventShortName', value);
        } else {
            formik.setFieldValue('eventShortName', value.substring(0, characterLimit));
            setCountCharacter(characterLimit);
        }
    }

    // handle the mode of enagagement selection
    function handleModeOfEventSelection(e) {
        const isPhysicalEvent = e.target.value === 'true';
        formik.setFieldValue('isPhysicalEvent', isPhysicalEvent);
    }

    // load the subcategory based on the selected category
    // whenever the category changes, the subcategory will be clear and again load the subcategory
    useEffect(() => {
        const selectedCategoryId = formik.values?.category?.id || formik.values?.category;

        // If category ID is 21 (which is "other"), set empty subcategories
        if (selectedCategoryId === 21) {
            setFilteredSubcategories([]);
            // formik.setFieldValue('subCategory', []);
            return;
        }

        // Otherwise keep existing functionality
        const filtered = subCategories.filter(
            (subcategory) => subcategory.category_id === selectedCategoryId
        );
        setFilteredSubcategories(filtered);

        // If the selected subcategories are not in the filtered list, reset them
        const validSubCategories = formik.values.subCategory.filter((sub) =>
            filtered.some((f) => f.id === sub.id)
        );

        if (validSubCategories.length !== formik.values.subCategory.length) {
            formik.setFieldValue('subCategory', validSubCategories);
        }
    }, [formik.values?.category, subCategories]);

    // handle the format filter options
    function handleFormatFilterOptions(options, params) {
        const filtered = filter(options, params);
        const { inputValue } = params;

        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option.name);
        if (inputValue !== '' && !isExisting) {
            filtered.push({
                inputValue,
                name: `Add "${inputValue}"`,
                isUserCreated: true
            });
        }

        return filtered;
    }

    // handle the format option change
    function handleFormatOptionChange(event, newValue) {
        if (typeof newValue === 'string') {
            formik.setFieldValue('format', newValue);
        } else if (newValue && newValue.inputValue) {
            formik.setFieldValue('format', newValue.inputValue);
        } else {
            formik.setFieldValue('format', newValue);
        }
    }

    // Function to get the label for format options
    function getFormatOptionLabel(option) {
        if (option.id === 39 && option.value) {
            return option.value;
        }

        if (typeof option === 'string') {
            return option;
        }
        if (option.inputValue) {
            return option.inputValue;
        }
        return option.name;
    }

    // handle the category option change
    function handleCategoryOptionChange(event, newValue) {
        if (typeof newValue === 'string') {
            // User added a custom category as a string
            formik.setFieldValue('category', { id: 21, value: newValue });
        } else if (newValue && newValue.inputValue) {
            // User added a custom category via inputValue
            formik.setFieldValue('category', { id: 21, value: newValue.inputValue });
        } else {
            // User selected an existing category
            formik.setFieldValue('category', newValue);
        }
    }

    // handle the category filter options
    function handleCategoryFilterOptions(options, params) {
        const filtered = filter(options, params);
        const { inputValue } = params;

        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option.name);
        if (inputValue !== '' && !isExisting) {
            filtered.push({
                inputValue,
                name: `Add ${inputValue}`,
                isUserCreated: true
            });
        }

        return filtered;
    }

    // Function to get the label for category options
    function getCategoryOptionLabel(option) {
        if (option.id === 21 && option.value) {
            return option.value;
        }

        if (typeof option === 'string') {
            return option; // For free text input
        }
        if (option.inputValue) {
            return `Add "${option.inputValue}"`; // For dynamically added options
        }
        if (option.value) {
            return option.value; // For custom categories
        }
        return option.name || ''; // For existing categories
    }

    // handle the subcategory option change
    function handleSubCategoryOptionChange(event, newValue) {
        if (typeof newValue === 'string') {
            formik.setFieldValue('subCategory', newValue);
        } else if (newValue && newValue.inputValue) {
            formik.setFieldValue('subCategory', newValue.inputValue);
        } else {
            formik.setFieldValue('subCategory', newValue);
        }
    }

    // handle the subcategory filter options
    function handleSubCategoryFilterOptions(options, params) {
        const filtered = filter(options, params);
        const { inputValue } = params;

        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option.name);
        if (inputValue !== '' && !isExisting) {
            filtered.push({
                inputValue,
                name: `Add "${inputValue}"`,
                isUserCreated: true
            });
        }

        return filtered;
    }

    // Function to get the label for subcategory options
    function getSubCategoryOptionLabel(option) {
        if (option.id === 182 && option.value) {
            return option.value;
        }

        if (typeof option === 'string') {
            return option; // For free text input
        }
        if (option.inputValue) {
            return option.inputValue; // For dynamically added options
        }
        return option.name || ''; // For existing subcategories
    }

    // return all the created variables, functions, state etc... that required
    return {
        formik,
        loading,
        isEditModeOn,
        characterLimit,
        countCharacter,
        shouldShowEditButton,
        filteredSubcategories,
        setIsEditModeOn,
        handleShortNameChange,
        handleModeOfEventSelection,
        handleFormatFilterOptions,
        handleFormatOptionChange,
        getFormatOptionLabel,
        handleCategoryFilterOptions,
        getCategoryOptionLabel,
        handleCategoryOptionChange,
        handleSubCategoryFilterOptions,
        getSubCategoryOptionLabel,
        handleSubCategoryOptionChange
    };
}

// Yup validation schema for the form
const validationSchema = Yup.object().shape({
    eventName: Yup.string().required('Event name is required'),
    category: Yup.mixed().required('Category is required'),
    isEventPrivate: Yup.boolean().required('Event visibility selection is required'),
    eventShortName: Yup.string()
        .required('Event short name is required')
        .max(40, 'Event short name should be less than 40 characters'),
    format: Yup.array()
        .min(1, 'Select at least 1 event formats')
        .max(3, 'Select at least 3 event formats')
        .required('Event formats are required'),
    subCategory: Yup.array()
        .min(1, 'Select at least 1 event sub categories')
        .max(5, 'Select at least 5 event sub categories')
        .required('Event sub categories are required'),
    eventCreatedAs: Yup.object(),
    isPhysicalEvent: Yup.boolean().required('Mode of engagement is required')
});

// Character limit for event short name
const characterLimit = 40;
